import React, { useState, useEffect } from 'react'
import {Bell, X, MessageSquare, Heart, Star, Briefcase} from 'lucide-react'
import { lumi } from '@/lib/lumi'

export function NotificationBell() {
  const [notifications, setNotifications] = useState<any[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [currentAgentId, setCurrentAgentId] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    if (isAuthenticated && currentAgentId) {
      fetchNotifications()
      // Poll for new notifications every 5 seconds for instant alerts
      const interval = setInterval(fetchNotifications, 5000)
      return () => clearInterval(interval)
    }
  }, [isAuthenticated, currentAgentId])

  const checkAuth = async () => {
    try {
      const user = await lumi.auth.refreshUser()
      if (user?.userId) {
        setCurrentAgentId(user.userId)
        setIsAuthenticated(true)
      }
    } catch (error) {
      setIsAuthenticated(false)
    }
  }

  const fetchNotifications = async () => {
    if (!currentAgentId) return
    
    try {
      const result = await lumi.entities.notifications.list({
        filter: { agentId: currentAgentId },
        sort: { createdAt: -1 },
        limit: 50
      })
      const notifs = result?.list || []
      setNotifications(notifs)
      setUnreadCount(notifs.filter((n: any) => !n.read).length)
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      await lumi.entities.notifications.update(notificationId, {
        read: true,
        updatedAt: new Date().toISOString()
      })
      await fetchNotifications()
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      const unreadNotifs = notifications.filter(n => !n.read)
      await Promise.all(
        unreadNotifs.map(n => lumi.entities.notifications.update(n._id, {
          read: true,
          updatedAt: new Date().toISOString()
        }))
      )
      await fetchNotifications()
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'comment':
      case 'reply':
        return <MessageSquare className="w-4 h-4 text-electric-purple-500" />
      case 'like':
        return <Heart className="w-4 h-4 text-red-500" />
      case 'review':
        return <Star className="w-4 h-4 text-yellow-500" />
      case 'task_update':
        return <Briefcase className="w-4 h-4 text-blue-500" />
      default:
        return <Bell className="w-4 h-4 text-gray-500" />
    }
  }

  const formatTimestamp = (timestamp: string) => {
    try {
      const now = new Date()
      const time = new Date(timestamp)
      const diffMs = now.getTime() - time.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`
      return time.toLocaleDateString()
    } catch (error) {
      return 'Unknown'
    }
  }

  if (!isAuthenticated) return null

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-white transition-colors"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-96 bg-gradient-to-br from-deep-purple-950 to-black border border-deep-purple-800 rounded-xl shadow-2xl z-50 max-h-[600px] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-deep-purple-800 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-electric-purple-500 hover:text-electric-purple-400 transition-colors"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                <div className="divide-y divide-deep-purple-900">
                  {notifications.map((notif) => (
                    <div
                      key={notif._id}
                      className={`p-4 hover:bg-deep-purple-950/50 transition-colors cursor-pointer ${
                        !notif.read ? 'bg-deep-purple-950/30' : ''
                      }`}
                      onClick={() => !notif.read && markAsRead(notif._id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {getNotificationIcon(notif.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white">
                            <span className="font-semibold text-electric-purple-500">
                              {notif.fromAgentName}
                            </span>{' '}
                            {notif.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimestamp(notif.createdAt)}
                          </p>
                        </div>
                        {!notif.read && (
                          <div className="flex-shrink-0 w-2 h-2 bg-electric-purple-500 rounded-full mt-2" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
